import { Component, computed, effect, inject, Input, Signal, signal } from '@angular/core';
import { combineLatest, distinctUntilChanged, map, Observable, shareReplay, startWith, Subject, switchMap } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Result, toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { PaginatedApiResponse } from '@lib/api/responses/paginated-api-response';
import { Package } from '@lib/api/models/package';
import { PackageService } from '@lib/package/package.service';
import { ComponentsModule, FormFieldModule, SnackBarService, SuffixDirective } from '@mastechnicaegroup/carelinelive-ui';
import { ErrorPanelComponent } from '@lib/errors/error-panel/error-panel.component';
import { PaginationCardComponent } from '@shared/pagination/pagination-card.component';
import { AppDatePipe } from '@shared/pipes/date.pipe';
import { postParamNamesFilterQuery } from '@shared/filters/filter-params';
import { AuthenticationService } from '@lib/auth/authentication.service';

@Component({
    selector: 'app-dashboard-configurable-package-table',
    imports: [
        FormFieldModule,
        ReactiveFormsModule,
        SuffixDirective,
        ErrorPanelComponent,
        PaginationCardComponent,
        ComponentsModule
    ],
    templateUrl: './dashboard-configurable-package-table.component.html',
    styleUrl: './dashboard-configurable-package-table.component.scss'
})
export class DashboardConfigurablePackageTableComponent {
    @Input() config!: Signal<Record<string, string | number | null>>;
    @Input() dataSrc: 'my-packages' | 'potential-packages' = 'my-packages';
    @Input() columns!: { key: string, value: string }[];
    @Input() title!: string;
    @Input() subTitle!: string;
    @Input() linkTo!: 'package' | 'provider-offer';

    private readonly _authService = inject(AuthenticationService);
    private readonly _packageService = inject(PackageService);
    private readonly _snackBar = inject(SnackBarService);

    protected user = toSignal(this._authService.user$);

    /** The current page being shown */
    protected readonly page = signal<number>(1);

    private config$ = new Subject<Record<string, string | number | null>>();

    /** The search form control */
    protected readonly searchControl: FormControl<string> = new FormControl<string>('', { nonNullable: true });

    /** Fetch the page of packages */
    protected readonly pageResult: Signal<Result<PaginatedApiResponse<string[]>> | undefined> = toSignal(combineLatest({
        page: toObservable(this.page),
        // join the config observable with the search control changes observable
        queryParams: combineLatest([
            this.config$,
            this.searchControl.valueChanges.pipe(
                // start with the initial value
                startWith(this.searchControl.value),
                map(value => value.trim())
            )
        ]).pipe(
            map(([config, search]) => {
                const queryParams = { ...config };

                // if there's a search term, add it to the query params
                if (search) {
                    queryParams[postParamNamesFilterQuery] = search;
                }
                return queryParams;
            })
        )

    }).pipe(
        distinctUntilChanged((prev, cur) => {
            return JSON.stringify(prev) === JSON.stringify(cur);
        }),
        switchMap(({ page, queryParams }) => {
            // depending on the data source, call the relevant service method
            let packages$: Observable<PaginatedApiResponse<Package>>;
            switch (this.dataSrc) {
                case 'potential-packages':
                    packages$ = this._packageService.listPotentialPackages(page, queryParams);
                    break;
                case 'my-packages':
                    packages$ = this._packageService.listMyPackages(page, queryParams);
            }

            return packages$.pipe(
                // some columns require transforming data so check the columns array for the object key values
                map((response) => {
                    // map values in the data rows if required
                    const mappedData = response.data.map((item) => {
                        // newItem is a Package but can have extra transformed fields
                        // the first field is always the package id so we can enable the click behaviour
                        const newItem: string[] = [item.id ?? ''];
                        this.columns.forEach((column) => {
                            // map values that need transforming
                            switch (column.key) {
                                case 'accept_date': {
                                    // use appDate pipe to format data
                                    const acceptedResponse = item.package_responses?.find(pr => pr.response === 'accepted');
                                    if (acceptedResponse) {
                                        newItem.push(new AppDatePipe().transform(acceptedResponse.created_at, 'dd/MM/yyyy HH:mm'));
                                    } else {
                                        newItem.push('N/A');
                                    }
                                    break;
                                }
                                case 'post_code': {
                                    if (item.address) {
                                        newItem.push(item.address.post_code);
                                    } else {
                                        newItem.push('N/A');
                                    }
                                    break;
                                }
                                case 'address': {
                                    if (item.address) {
                                        newItem.push(`${item.address.street_1}, ${item.address.post_code}`);
                                    } else {
                                        newItem.push('N/A');
                                    }
                                    break;
                                }
                                case 'commissioner': {
                                    newItem.push(item.commissioner?.name ?? 'N/A');
                                    break;
                                }
                                case 'provider': {
                                    const providers = new Set(item.package_offer_assign?.map(po => po.provider?.name).filter(name => name != null));
                                    newItem.push(Array.from(providers).join(',') ?? 'N/A');
                                    break;
                                }
                                case 'comments': {
                                    // comments from offers or responses
                                    const comments = [
                                        ...(item.package_offer_assign??[]).map(po => po.additional_comments),
                                        ...(item.package_responses??[]).map(pr => pr.additional_comments)
                                    ].filter(c => (c??'').length > 0).join(', ');
                                    newItem.push(comments ?? 'None');
                                    break;
                                }
                                case 'requested_date': {
                                    const requestedResponse = item.package_responses?.find(pr => pr.response === 'requested');
                                    if (requestedResponse) {
                                        newItem.push(new AppDatePipe().transform(requestedResponse.created_at, 'dd/MM/yyyy HH:mm'));
                                    } else {
                                        newItem.push('N/A');
                                    }
                                    break;
                                }
                                case 'offer_date': {
                                    const latestOffer = [...(item.package_offer_assign ?? [])].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
                                    if (latestOffer) {
                                        newItem.push(new AppDatePipe().transform(latestOffer.created_at, 'dd/MM/yyyy HH:mm'));
                                    } else {
                                        newItem.push('N/A');
                                    }
                                    break;
                                }
                                case 'status': {
                                    // determine status based on package properties
                                    let status = 'Pending';
                                    if(item.provider?.id && this.user()?.provider?.id !== item.provider.id) {
                                        status = 'Unavailable';
                                    }
                                    newItem.push(status);
                                    break;
                                }
                                default: {
                                    const value = item[column.key as keyof Package];
                                    newItem.push(value != null ? String(value) : 'N/A');
                                    break;
                                }
                            }

                        });
                        return newItem;
                    });

                    return { ...response, data: mappedData };
                }),
                // Turn into a Result
                toResult()
            );
        }),

        // Share the same result with all new subscribers
        shareReplay(1)
    ));

    /** The pagination meta data */
    protected readonly meta = computed(() => {
        // Extract the meta-data from the page result
        return this.pageResult()?.value?.meta;
    });

    constructor() {
        effect(() => {
            this.config$.next(this.config());
        });
    }

    protected nextPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page + 1);
        }
    }

    protected previousPage(): void {
        const meta = this.meta();

        if (meta) {
            this.setPage(meta.current_page - 1);
        }
    }

    protected async setPage(page: number) {
        this.page.set(page);
    }

    openPackageInNewTab(packageId: string | undefined) {
        if(this.linkTo === 'package') {
            if (!packageId) {
                this._snackBar.open({
                    colour: 'error',
                    icon: 'error_outline',
                    title: 'Package not available'
                });
                return;
            }

            const url = `/packages/${packageId}`;
            window.open(url, '_blank');
        } else if(this.linkTo === 'provider-offer') {
            const url = `/providers/${this.user()?.provider?.id}?tab=2`;
            window.open(url, '_blank');
        }
    }
}
